import React from "react";
import { useTranslation } from "react-i18next";
import { useTelemetry } from "../../context/TelemetryContext";
import ModeIndicator from "../../components/shared/ModeIndicator";
import DemoDataNotice from "../../components/shared/DemoDataNotice";
import LanguageToggle from "../../components/shared/LanguageToggle";
import SubPageNav from "../../components/shared/SubPageNav";

function Forecasting() {
  const { t } = useTranslation();
  const telemetry = useTelemetry();

  let predictedRisk = "SAFE";
  let forecastConfidence = 65;

  if (telemetry.overallRisk === "CRITICAL") {
    predictedRisk = "CRITICAL";
    forecastConfidence = telemetry.aiConfidence || 92;
  } else if (telemetry.overallRisk === "WARNING") {
    predictedRisk = "CRITICAL";
    forecastConfidence = telemetry.aiConfidence || 78;
  } else if (telemetry.overallRisk === "ELEVATED") {
    predictedRisk = "WARNING";
    forecastConfidence = telemetry.aiConfidence || 65;
  } else {
    predictedRisk = "SAFE";
    forecastConfidence = telemetry.aiConfidence || 55;
  }

  const forecastSummary =
    telemetry?.overallRisk === "CRITICAL"
      ? t("forecasting.summary.critical")
      : telemetry?.overallRisk === "WARNING"
      ? t("forecasting.summary.warning")
      : telemetry?.overallRisk === "ELEVATED"
      ? t("forecasting.summary.elevated")
      : t("forecasting.summary.safe");

  const riskColor = (r) =>
    r === "CRITICAL" ? "#ef4444" : r === "WARNING" ? "#f59e0b" : r === "ELEVATED" ? "#38bdf8" : "#22c55e";

  return (
    <div style={{ padding: "20px", color: "white" }}>

      <div style={{ position: "relative", textAlign: "center", marginBottom: "10px" }}>
        <h1 style={{ margin: 0 }}>{t("forecasting.title")}</h1>
        <div style={{ position: "absolute", top: 0, right: 0, display: "flex", alignItems: "flex-start", gap: "12px" }}>
          <LanguageToggle />
          <ModeIndicator />
        </div>
      </div>

      <p style={{ textAlign: "center", color: "#94a3b8", marginBottom: "25px" }}>
        {t("forecasting.subtitle")}
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>

        <div style={{ background: "#111827", padding: "20px", borderRadius: "14px", border: "1px solid #1e293b" }}>
          <h3>{t("forecasting.currentState.title")}</h3>
          <h1 style={{ marginTop: "15px", color: riskColor(telemetry.overallRisk) }}>{t(`commandCenter.risk.${telemetry.overallRisk}`, telemetry.overallRisk)}</h1>
          <p style={{ color: "#94a3b8" }}>{t("forecasting.currentState.subtitle")}</p>
        </div>

        <div style={{ background: "#111827", padding: "20px", borderRadius: "14px", border: "1px solid #1e293b" }}>
          <h3>{t("forecasting.predictedState.title")}</h3>
          <h1 style={{ marginTop: "15px", color: riskColor(predictedRisk) }}>{t(`commandCenter.risk.${predictedRisk}`, predictedRisk)}</h1>
          <p style={{ color: "#94a3b8" }}>{t("forecasting.predictedState.confidence", { value: forecastConfidence })}</p>
        </div>

        <div style={{ background: "#111827", padding: "20px", borderRadius: "14px", border: "1px solid #1e293b" }}>
          <h3>{t("forecasting.predictionDrivers.title")}</h3>
          <div style={{ marginTop: "15px" }}>
            <p>{t("forecasting.predictionDrivers.pm25", { value: telemetry.pm25 })}</p>
            <p>{t("forecasting.predictionDrivers.voc", { value: telemetry.voc })}</p>
            <p>{t("forecasting.predictionDrivers.exposureSignal", { value: telemetry.fentanyl_ppb })}</p>
          </div>
        </div>

        <div style={{ background: "#111827", padding: "20px", borderRadius: "14px", border: "1px solid #1e293b" }}>
          <h3>{t("forecasting.digitalTwinSimulation.title")}</h3>
          <div style={{ marginTop: "15px" }}>
            <p>{t("forecasting.digitalTwinSimulation.facilityModel", { value: t("commandCenter.status.active") })}</p>
            <p>{t("forecasting.digitalTwinSimulation.simulationHorizon", { value: t("forecasting.digitalTwinSimulation.simulationHorizonValue") })}</p>
            <p>{t("forecasting.digitalTwinSimulation.status", { value: t("commandCenter.status.synchronized") })}</p>
          </div>
        </div>

      </div>

      <div style={{ marginTop: "25px", background: "#111827", padding: "25px", borderRadius: "14px", border: "1px solid #1e293b" }}>
        <h3 style={{ textAlign: "center", marginBottom: "20px" }}>{t("forecasting.timeline.title")}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: "12px" }}>
          <div style={{ background:"#0f172a", padding:"15px", borderRadius:"10px", textAlign:"center" }}>
            <h4>{t("forecasting.timeline.current")}</h4><h2 style={{ color: riskColor(telemetry.overallRisk) }}>{t(`commandCenter.risk.${telemetry.overallRisk}`, telemetry.overallRisk)}</h2>
          </div>
          <div style={{ background:"#0f172a", padding:"15px", borderRadius:"10px", textAlign:"center" }}>
            <h4>{t("forecasting.timeline.plus5")}</h4><h2 style={{ color: "#f59e0b" }}>{t("commandCenter.risk.WARNING")}</h2>
          </div>
          <div style={{ background:"#0f172a", padding:"15px", borderRadius:"10px", textAlign:"center" }}>
            <h4>{t("forecasting.timeline.plus15")}</h4><h2 style={{ color: riskColor(predictedRisk) }}>{t(`commandCenter.risk.${predictedRisk}`, predictedRisk)}</h2>
          </div>
          <div style={{ background:"#0f172a", padding:"15px", borderRadius:"10px", textAlign:"center" }}>
            <h4>{t("forecasting.timeline.plus30")}</h4><h2 style={{ color: "#ef4444" }}>{t("commandCenter.risk.CRITICAL")}</h2>
          </div>
          <div style={{ background:"#0f172a", padding:"15px", borderRadius:"10px", textAlign:"center" }}>
            <h4>{t("forecasting.timeline.confidence")}</h4><h2>{forecastConfidence}%</h2>
          </div>
        </div>
      </div>

      <div style={{ marginTop: "20px", background: "#111827", padding: "20px", borderRadius: "14px", border: "1px solid #1e293b" }}>
        <h3 style={{ textAlign: "center", marginBottom: "20px" }}>{t("forecasting.riskTrend.title")}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: "10px", alignItems: "end", height: "140px" }}>
          {[{time:"5m",value:35},{time:"10m",value:45},{time:"15m",value:52},{time:"20m",value:63},{time:"25m",value:75},{time:"30m",value:92}].map((item,index) => (
            <div key={index} style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-end" }}>
              <div style={{ width:"24px", height:`${item.value}px`, background: item.value>80?"#ef4444":item.value>60?"#f59e0b":"#22c55e", borderRadius:"4px" }} />
              <div style={{ marginTop:"8px", fontSize:"12px" }}>{item.value}</div>
              <div style={{ fontSize:"11px", color:"#94a3b8", marginTop:"4px" }}>{item.time}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop:"20px", background:"#111827", padding:"20px", borderRadius:"14px", border:"1px solid #1e293b" }}>
        <h3 style={{ textAlign:"center", marginBottom:"20px" }}>{t("forecasting.insights.title")}</h3>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:"15px" }}>
          <div style={{ textAlign:"center" }}><h4>{t("forecasting.insights.trendDirection")}</h4><p style={{ color:"#38bdf8" }}>{t("forecasting.insights.increasing")}</p></div>
          <div style={{ textAlign:"center" }}><h4>{t("forecasting.insights.patternMatch")}</h4><p style={{ color:"#22c55e" }}>81%</p></div>
          <div style={{ textAlign:"center" }}><h4>{t("forecasting.insights.predictedEscalation")}</h4><p style={{ color:"#f59e0b" }}>{t("forecasting.insights.predictedEscalationValue")}</p></div>
          <div style={{ textAlign:"center" }}>
            <h4>{t("forecasting.insights.forecastConfidence")}</h4>
            <p style={{ color: forecastConfidence>=80?"#22c55e":forecastConfidence>=60?"#f59e0b":"#ef4444" }}>{forecastConfidence}%</p>
          </div>
        </div>
        <div style={{ marginTop:"20px", padding:"20px", background:"#0f172a", borderRadius:"14px", border:"1px solid #1e293b" }}>
          <h3 style={{ textAlign:"center", marginBottom:"20px" }}>{t("forecasting.recommendation.title")}</h3>
          <div style={{ textAlign:"center", fontSize:"18px", fontWeight:"bold", color: riskColor(predictedRisk) }}>
            {predictedRisk==="CRITICAL"?t("forecasting.recommendation.critical"):predictedRisk==="WARNING"?t("forecasting.recommendation.warning"):t("forecasting.recommendation.safe")}
          </div>
        </div>
      </div>

      <div style={{ marginTop:"20px", background:"#111827", padding:"20px", borderRadius:"14px", border:"1px solid #1e293b" }}>
        <h3 style={{ textAlign:"center", marginBottom:"20px" }}>{t("forecasting.status.title")}</h3>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:"15px" }}>
          <div style={{ textAlign:"center" }}><h4>{t("forecasting.status.status")}</h4><p style={{ color:"#22c55e" }}>{t("commandCenter.status.active")}</p></div>
          <div style={{ textAlign:"center" }}><h4>{t("forecasting.status.modelVersion")}</h4><p>v2.4</p></div>
          <div style={{ textAlign:"center" }}><h4>{t("forecasting.status.engine")}</h4><p>{t("commandCenter.status.running")}</p></div>
          <div style={{ textAlign:"center" }}><h4>{t("forecasting.status.lastForecast")}</h4><p>{t("forecasting.status.lastForecastValue")}</p></div>
          <div style={{ textAlign:"center" }}><h4>{t("forecasting.status.accuracy")}</h4><p style={{ color:"#38bdf8" }}>94%</p><DemoDataNotice textKey="common.demoDataNotice.illustrativeFigure" /></div>
        </div>
      </div>

      <div style={{ marginTop:"20px", background:"#111827", padding:"20px", borderRadius:"14px", border:"1px solid #1e293b" }}>
        <h3 style={{ textAlign:"center", marginBottom:"20px" }}>{t("forecasting.confidenceMeter.title")}</h3>
        <div style={{ width:"100%", height:"20px", background:"#0f172a", borderRadius:"20px", overflow:"hidden" }}>
          <div style={{ width:`${forecastConfidence}%`, height:"100%", background: forecastConfidence>=80?"#22c55e":forecastConfidence>=60?"#f59e0b":"#ef4444" }} />
        </div>
        <div style={{ textAlign:"center", marginTop:"12px", fontSize:"20px", fontWeight:"bold" }}>{forecastConfidence}%</div>
      </div>

      <div style={{ background:"#111827", padding:"30px", borderRadius:"14px", border:"1px solid #1e293b", marginTop:"20px" }}>
        <h3 style={{ textAlign:"center", marginBottom:"20px" }}>{t("forecasting.summary.title")}</h3>
        <p style={{ color:"#94a3b8", textAlign:"center", lineHeight:"1.8", maxWidth:"1000px", margin:"0 auto" }}>{forecastSummary}</p>
      </div>

      <SubPageNav current="forecasting" />

    </div>
  );
}

export default Forecasting;
